import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowDownToLine, Search, ScanBarcode } from "lucide-react";

export default function CaricoPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const preId = sp.get("product");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ quantita: 1, documento: "", fornitore: "", note: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (preId) {
      (async () => {
        const { data } = await api.get(`/products/${preId}`);
        setSelected(data);
        setForm(s => ({ ...s, fornitore: data.marca }));
      })();
    }
  }, [preId]);

  const search = async (q) => {
    if (!q || q.length < 1) { setResults([]); return; }
    const { data } = await api.get("/products", { params: { q } });
    setResults(data.slice(0, 8));
  };

  const pick = (p) => {
    setSelected(p); setResults([]); setQuery("");
    setForm(s => ({ ...s, fornitore: p.marca }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post("/movements", {
        product_id: selected.id, tipo: "carico",
        quantita: parseInt(form.quantita),
        documento: form.documento, fornitore: form.fornitore, note: form.note,
      });
      toast.success(`Carico registrato. Nuova giacenza: ${data.giacenza_dopo}`);
      nav(`/prodotti/${selected.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 bg-[#4CAF50]/10 border border-[#4CAF50]/30 flex items-center justify-center">
          <ArrowDownToLine className="text-[#4CAF50]" size={22} />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Operazione</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight">Carico Merce</h1>
        </div>
      </div>

      {!selected ? (
        <div className="gm-card p-6">
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Cerca prodotto (codice interno, fornitore, descrizione)</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
            <input
              data-testid="carico-search-input"
              autoFocus
              value={query}
              onChange={(e)=>{ setQuery(e.target.value); search(e.target.value); }}
              placeholder="Es. FIL-WAC-0001 o 100045678"
              className="gm-input pl-11"
            />
          </div>
          <button onClick={()=>nav("/scanner?dest=carico")} className="mt-3 inline-flex items-center gap-2 text-sm text-[#4CAF50] uppercase tracking-widest">
            <ScanBarcode size={16} /> oppure scansiona barcode
          </button>
          {results.length > 0 && (
            <div className="mt-4 border border-zinc-800 divide-y divide-zinc-800/50">
              {results.map(p => (
                <button key={p.id} onClick={()=>pick(p)} className="w-full text-left p-3 hover:bg-zinc-900 flex items-center justify-between" data-testid="carico-search-result">
                  <div>
                    <div className="font-mono-data text-xs text-[#4CAF50]">{p.codice_interno}</div>
                    <div className="text-sm font-semibold">{p.descrizione}</div>
                    <div className="text-xs text-zinc-500">{p.marca} • {p.posizione || "—"}</div>
                  </div>
                  <div className="font-mono-data text-zinc-500">{p.giacenza}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="gm-card p-6 space-y-5">
          <div className="border-l-4 border-[#4CAF50] pl-4 mb-2">
            <div className="font-mono-data text-xs text-[#4CAF50]">{selected.codice_interno}</div>
            <div className="font-semibold text-white">{selected.descrizione}</div>
            <div className="text-xs text-zinc-500">Giacenza attuale: <span className="font-mono-data text-white">{selected.giacenza}</span> • {selected.marca}</div>
            <button type="button" onClick={()=>setSelected(null)} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white mt-1">cambia prodotto</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Quantità in carico *</label>
              <input data-testid="carico-quantita" type="number" min="1" required value={form.quantita} onChange={e=>setForm(s=>({...s, quantita: e.target.value}))} className="gm-input font-mono-data text-2xl" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Documento (DDT / Fattura)</label>
              <input data-testid="carico-documento" value={form.documento} onChange={e=>setForm(s=>({...s, documento: e.target.value}))} className="gm-input" placeholder="es. DDT 1234/2026" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Fornitore</label>
              <input value={form.fornitore} onChange={e=>setForm(s=>({...s, fornitore: e.target.value}))} className="gm-input" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Note</label>
              <textarea rows="2" value={form.note} onChange={e=>setForm(s=>({...s, note: e.target.value}))} className="gm-input py-2 h-auto resize-none" />
            </div>
          </div>

          <button type="submit" disabled={busy} data-testid="carico-submit-button" className="gm-btn-primary w-full h-14 rounded-sm flex items-center justify-center gap-2">
            <ArrowDownToLine size={20} />
            {busy ? "Salvataggio..." : `Conferma carico di ${form.quantita} pz`}
          </button>
        </form>
      )}
    </div>
  );
}
